import { defineStore } from 'pinia'
import type { Regelgruppe, RegelgruppeMeta } from '@/stores/regelgruppen/Regelgruppe'
import { computed, readonly, type Ref, ref, watch } from 'vue'
import { useSessionStorage } from '@vueuse/core'
import { addLazyQueryParamsToUrl, type LazyQueryParams } from '@/stores/regelgruppen/LazyParams'
import { useStammdatenApi } from '@/composables/stammdatenApiComposable'
import type { RegelgruppenMetaResponse } from '@/stores/regelgruppen/RegelgruppenMetaResponse'
import type {
  CreateRegelgruppeRequest,
  UpdateRegelgruppeRequest
} from '@/stores/regelgruppen/UpdateRegelgruppeRequest'
import { useToast } from 'primevue/usetoast'
import { t } from '@/i18n'
import { usePatientenProfileStore } from '@/stores/patientenprofile/PatientenprofileStore'
import type { Regelposition } from '@/stores/regeln/models/Regelposition'
import { useRegelnStore } from '@/stores/regeln/RegelnStore'
import { useKeimeStore } from '@/stores/keime/KeimeStore'
import type { Keim } from '@/stores/keime/Keim'
import type { Keimgruppe } from '@/stores/keimgruppen/Keimgruppe'
import { useKeimgruppenStore } from '@/stores/keimgruppen/KeimgruppeStore'
import { useFaecherHistory } from '@/composables/windowHistoryComposable'

const updateDataInRegelgruppenById = (
  regelgruppen: Ref<RegelgruppenMetaResponse[]>,
  data: RegelgruppenMetaResponse
) => {
  const index = regelgruppen.value.findIndex((r) => r.id === data.id)
  if (index === -1) {
    return false
  } else {
    regelgruppen.value[index] = data
    return true
  }
}

export const useRegelgruppenStore = defineStore('regelgruppen', () => {
  const stammdatenApi = useStammdatenApi()
  const keimStore = useKeimeStore()
  const keimGruppeStore = useKeimgruppenStore()
  const patientenProfilStore = usePatientenProfileStore()
  const regelnStore = useRegelnStore()
  const toast = useToast()
  const faecherHistory = useFaecherHistory()

  const regelgruppenState = useSessionStorage<RegelgruppenMetaResponse[]>('regelgruppen', [])
  const regelgruppenNamesState = ref(
    regelgruppenState.value.map((r) => ({ name: r.name, id: r.id }))
  )
  const regelgruppenWithActiveFilter = ref<RegelgruppenMetaResponse[]>([])
  const selectedItemId = ref<string>()
  const selectedDetails = ref<Regelgruppe>()
  const isLoadingState = ref(false)
  const isDetailsLoading = ref(false)
  const activeQuery = ref<LazyQueryParams>()
  const totalCount = ref<number>(0)

  const updateRegelgruppeInBothStates = (regelgruppe: Regelgruppe) => {
    if (selectedItemId.value === regelgruppe.id) selectedDetails.value = regelgruppe
    const existsInState = updateDataInRegelgruppenById(regelgruppenState, regelgruppe)
    const existsInFilter = updateDataInRegelgruppenById(regelgruppenWithActiveFilter, regelgruppe)
    return { existsInFilter, existsInState }
  }

  const addRegelgruppe = async (values: CreateRegelgruppeRequest) => {
    const req = stammdatenApi('Regelgruppen').post(values).json<Regelgruppe>()
    await req
    if (req.error.value) throw new Error(t('regelgruppenView.detail.createFailed'))
    const saved = req.data.value!
    regelgruppenState.value.push(saved)
    regelgruppenWithActiveFilter.value.unshift(saved)
    totalCount.value += 1
    selectedItemId.value = saved.id
    selectedDetails.value = saved
    return saved
  }

  const setQueryParams = (queryParams: LazyQueryParams | undefined) => {
    activeQuery.value = queryParams
    if (!queryParams) return
    void fetchRegelgruppenByQueryParams().catch(() => (activeQuery.value = undefined))
  }

  const fetchRegelgruppenByQueryParams = async () => {
    const req = stammdatenApi<{ totalCount: number; payload: RegelgruppenMetaResponse[] }>(
      addLazyQueryParamsToUrl('Regelgruppen', activeQuery.value)
    )
      .get()
      .json<{ totalCount: number; payload: RegelgruppenMetaResponse[] }>()
    const { isFetching, data, error } = req
    const unwatch = watch(isFetching, (v) => (isLoadingState.value = v))
    await req
    unwatch()
    if (error.value || !data.value) throw error.value
    data.value.payload.forEach((r: RegelgruppenMetaResponse) => {
      const i = regelgruppenState.value.findIndex((rState) => rState.id === r.id)
      if (i !== -1) regelgruppenState.value[i] = { ...regelgruppenState.value[i], ...r }
      else regelgruppenState.value.push(r)
    })
    regelgruppenWithActiveFilter.value = data.value.payload
    totalCount.value = data.value.totalCount
  }

  const getRegelgruppenNames = async () => {
    const req = stammdatenApi('Regelgruppen/Names').get().json<RegelgruppeMeta[]>()
    const { isFetching, data, error } = req
    const unwatch = watch(isFetching, (v) => (isDetailsLoading.value = v))
    await req
    unwatch()
    if (error.value || !data.value) return
    regelgruppenNamesState.value = data.value
  }

  const fetchRegelgruppeById = async (id: string) => {
    const req = stammdatenApi<Regelgruppe>(`Regelgruppen/${id}`).get().json<Regelgruppe>()
    const { isFetching, data, error } = req
    const unwatch = watch(isFetching, (v) => (isDetailsLoading.value = v))
    await req
    unwatch()
    if (error.value) throw new Error(t('regelgruppenView.detail.loadFailed'))
    const { existsInState } = updateRegelgruppeInBothStates(data.value!)
    if (!existsInState) {
      regelgruppenState.value.push(data.value!)
    }
    return data.value!
  }

  const selectRegelgruppe = async (id: string | undefined) => {
    if (!id) {
      selectedItemId.value = undefined
      selectedDetails.value = undefined
      faecherHistory.updateHistory()
      return
    }
    selectedItemId.value = id
    await patientenProfilStore.selectPatientenProfile(undefined)
    void regelnStore.selectRegel(undefined)
    await fetchRegelgruppeById(id)
    faecherHistory.updateHistory()
  }

  const deleteRegelgruppe = async (id: string) => {
    const regelgruppeIndex = regelgruppenState.value.findIndex((r) => r.id === id)
    const regelgruppeToDelete = regelgruppenState.value.at(regelgruppeIndex)
    if (regelgruppeToDelete) regelgruppenState.value.splice(regelgruppeIndex, 1)

    const activeFilterIndex = regelgruppenWithActiveFilter.value.findIndex((r) => r.id === id)
    const activeFilterToDelete = regelgruppenWithActiveFilter.value.at(activeFilterIndex)
    if (activeFilterToDelete) regelgruppenWithActiveFilter.value.splice(regelgruppeIndex, 1)

    const req = stammdatenApi(`Regelgruppen/${id}`).delete()
    const { error } = req

    if (selectedItemId.value === id) selectedItemId.value = undefined
    await req
    if (error.value) {
      if (regelgruppeToDelete)
        regelgruppenState.value.splice(regelgruppeIndex, 0, regelgruppeToDelete)
      if (activeFilterToDelete)
        regelgruppenState.value.splice(regelgruppeIndex, 0, activeFilterToDelete)
    }
  }

  const copyItem = async (
    id: string,
    name: string,
    beschreibung: string,
    befundbereich: string
  ) => {
    const req = stammdatenApi<Regelgruppe>(`Regelgruppen/Copy/${id}`)
      .post({ name, beschreibung, befundbereich })
      .json<Regelgruppe>()
    const { data, error, isFetching } = req
    const unwatch = watch(isFetching, (v) => (isLoadingState.value = v))
    await req
    unwatch()
    if (error.value || !data.value) return
    regelgruppenWithActiveFilter.value.unshift(data.value)
    regelgruppenState.value.unshift(data.value)
    totalCount.value = regelgruppen.value.length
  }

  const updateRegelgruppe = async (values: UpdateRegelgruppeRequest) => {
    const req = stammdatenApi('Regelgruppen').put(values).json<Regelgruppe>()
    await req
    if (req.error.value) throw new Error(t('regelgruppenView.detail.saveFailed'))
    const saved = req.data.value!
    updateRegelgruppeInBothStates(saved)
    if (selectedItemId.value === saved.id) {
      const regelId = regelnStore.selectedItem?.id
      await patientenProfilStore.reloadPatientenProfile()
      regelnStore.selectRegel(regelId)
    }
    return saved
  }

  const addKeimFromSelectedItem = async (keim: Keim) => {
    if (!selectedItem.value) return
    const k = keimStore.keime.find((k) => k.kuerzel === keim.kuerzel)
    if (!k) return

    const newKeim = {
      id: k.id,
      kuerzel: k.kuerzel,
      name: k.name
    } as Keim
    const rg = { ...selectedItem.value }
    if (!rg.keime) {
      rg.keime = []
    }
    rg.keime.push(newKeim)
    updateRegelgruppeInBothStates(rg)

    const req = stammdatenApi('Regelgruppen/Keim')
      .post({
        regelgruppenId: selectedItem.value.id,
        id: newKeim.id
      })
      .json<Regelgruppe>()
    const { data, error } = req
    await req
    if (error.value) {
      const rg = { ...selectedItem }
      if (!rg.value) return
      rg.value.keime = rg.value.keime.filter((x) => x.id !== keim.id)
      if (data.value) updateRegelgruppeInBothStates(data.value)
    }
    const rg2 = { ...selectedItem.value }
    if (!rg2) return
    updateRegelgruppeInBothStates(rg2)
    const selectedRegel = regelnStore.selectedItem
    await patientenProfilStore.reloadPatientenProfile()
    void regelnStore.selectRegel(selectedRegel?.id)
  }

  const addKeimgruppeFromSelectedItem = async (keimgruppe: Keimgruppe) => {
    if (!selectedItem.value) return
    const k = keimGruppeStore.keimgruppen.find((k) => k.kuerzel === keimgruppe.kuerzel)
    if (!k) return

    const rg = { ...selectedItem.value }
    if (!rg.keimgruppen) {
      rg.keimgruppen = []
    }
    rg.keimgruppen.push(keimgruppe)
    updateRegelgruppeInBothStates(rg)

    const req = stammdatenApi('Regelgruppen/Keimgruppe')
      .post({
        regelgruppenId: selectedItem.value.id,
        id: keimgruppe.id
      })
      .json<Regelgruppe>()
    const { data, error } = req
    await req
    if (error.value) {
      const rg = { ...selectedItem }
      if (!rg.value) return
      rg.value.keimgruppen = ((rg.value.keimgruppen as Keimgruppe[] | undefined) ?? []).filter(
        (x) => x.id !== keimgruppe.id
      )
      if (data.value) updateRegelgruppeInBothStates(data.value)
    }
    const rg2 = { ...selectedItem.value }
    if (!rg2) return
    updateRegelgruppeInBothStates(rg2)
    const selectedRegel = regelnStore.selectedItem
    await patientenProfilStore.reloadPatientenProfile()
    void regelnStore.selectRegel(selectedRegel?.id)
  }

  const deleteKeimFromSelectedItem = async (keimId: string) => {
    const regelgruppenId = selectedItem.value?.id
    const isUsed = await checkKeimUsedInSelected(keimId)
    if (!regelgruppenId) return
    if (isUsed.length > 0) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: t('errors.deleteKeimIsInUse'),
        life: 3000
      })

      return
    }
    const req = stammdatenApi(`Regelgruppen/Keim`).delete({
      id: keimId,
      regelgruppenId: selectedItem.value.id
    })
    const { error } = req
    await req
    if (error.value) return
    const i = regelgruppenState.value.findIndex((r) => r.id === regelgruppenId)
    if (i === -1) return
    regelgruppenState.value[i].keime =
      regelgruppenState.value[i].keime?.filter((a) => a.id !== keimId) ?? []
    //update regelpositionen and used analysen by updating patientenprofil
    const selectedRegel = regelnStore.selectedItem
    await patientenProfilStore.reloadPatientenProfile()
    void regelnStore.selectRegel(selectedRegel?.id)
  }

  const deleteKeimgruppeFromSelectedItem = async (keimgruppenId: string) => {
    const regelgruppenId = selectedItem.value?.id
    const isUsed = await checkKeimgruppeUsedInSelected(keimgruppenId)
    if (!regelgruppenId) return
    if (isUsed.length > 0) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: t('errors.deleteKeimgruppeIsInUse'),
        life: 3000
      })

      return
    }
    const req = stammdatenApi(`Regelgruppen/Keimgruppe`).delete({
      id: keimgruppenId,
      regelgruppenId: selectedItem.value.id
    })
    const { error } = req
    await req
    if (error.value) return
    const i = regelgruppenState.value.findIndex((r) => r.id === regelgruppenId)
    if (i === -1) return
    regelgruppenState.value[i].keimgruppen = (
      (regelgruppenState.value[i].keimgruppen as Keimgruppe[] | undefined) ?? []
    ).filter((a) => a.id !== keimgruppenId)
    const selectedRegel = regelnStore.selectedItem
    await patientenProfilStore.reloadPatientenProfile()
    void regelnStore.selectRegel(selectedRegel?.id)
  }

  const setLockKeimFromSelectedItem = async (keimId: string, isLocked: boolean) => {
    const selItem = selectedItem.value
    if (!selItem) return
    const i = selItem.analysen.findIndex((a) => a.id === keimId)
    if (i === -1) return
    const isLockedOld = selItem.analysen[i].isLocked
    selItem.keime[i].isLocked = isLocked
    const req = stammdatenApi('Regelgruppen/Keim').put({
      regelgruppenId: selItem.id,
      keimId,
      isLocked: isLocked
    })
    const { error } = req
    await req
    if (!error.value) return
    selItem.keime[i].isLocked = isLockedOld
  }

  const checkAnalyseUsage = async (regelgruppenId: string, analyseId: string, vorwert: boolean) => {
    const path = vorwert ? 'Analyse/Vorwert/isUsed' : 'Analyse/isUsed'
    const req = stammdatenApi(
      `Regelgruppen/${path}?regelgruppenId=${regelgruppenId}&analyseId=${analyseId}`
    )
      .get()
      .json<string[]>()
    await req
    if (req.error.value) throw new Error(t('regelgruppenView.detail.usageCheckFailed'))
    return req.data.value!
  }

  const checkKeimUsedInSelected = async (keimId: string): Promise<string[]> => {
    try {
      // Warte auf die API-Antwort
      const req = await stammdatenApi(
        `Regelgruppen/Keim/isUsed?regelgruppenId=${selectedItemId.value}&keimId=${keimId}`
      ).get()

      const { data, error } = req

      if (error?.value) {
        console.error('API Fehler:', error.value)
        return []
      }

      if (!data) {
        console.error('Keine Daten zurückgegeben')
        return []
      }
      const parsedData = (
        typeof data.value === 'string' ? JSON.parse(data.value) : data.value
      ) as unknown
      const i = Array.isArray(parsedData) ? parsedData.map(String) : []
      return i
    } catch (err) {
      console.error('API Anfrage fehlgeschlagen:', err)
      return []
    }
  }

  const checkKeimgruppeUsedInSelected = async (keimgruppenId: string): Promise<string[]> => {
    try {
      const req = await stammdatenApi(
        `Regelgruppen/Keimgruppe/isUsed?regelgruppenId=${selectedItemId.value}&keimgruppenId=${keimgruppenId}`
      ).get()

      const { data, error } = req

      if (error?.value) {
        console.error('API Fehler:', error.value)
        return []
      }

      if (!data) {
        console.error('Keine Daten zurückgegeben')
        return []
      }
      const parsedData = (
        typeof data.value === 'string' ? JSON.parse(data.value) : data.value
      ) as unknown
      const i = Array.isArray(parsedData) ? parsedData.map(String) : []
      return i
    } catch (err) {
      console.error('API Anfrage fehlgeschlagen:', err)
      return []
    }
  }

  const handleRegelpositionUpdated = (rp: Regelposition) => {
    const toAdd = rp.regelSpezifikation !== undefined
    const pp = selectedItem.value?.patientenProfile?.find(
      (x) => x.id == patientenProfilStore.selectedItem?.id
    )
    if (!pp || !toAdd) return
  }

  const regelgruppen = computed(() => {
    return activeQuery.value ? regelgruppenWithActiveFilter.value : regelgruppenState.value
  })

  const allRegelgruppen = computed(() => regelgruppenState.value)
  const selectedItem = computed(() =>
    selectedItemId.value === selectedDetails.value?.id ? selectedDetails.value : undefined
  )

  return {
    isLoading: isLoadingState,
    isDetailsLoading: readonly(isDetailsLoading),
    regelgruppen,
    allRegelgruppen,
    regelgruppenNames: readonly(regelgruppenNamesState),
    selectedItem: selectedItem,
    totalCount,
    activeQuery,
    fetchRegelgruppen: setQueryParams,
    addRegelgruppe,
    selectRegelgruppe,
    deleteRegelgruppe,
    copyItem,
    updateRegelgruppe,
    addKeimFromSelectedItem,
    addKeimgruppeFromSelectedItem,
    deleteKeimFromSelectedItem,
    deleteKeimgruppeFromSelectedItem,
    setLockKeimFromSelectedItem,
    getRegelgruppenNames,
    fetchRegelgruppeById,
    regelgruppenWithActiveFilter,
    regelgruppenState,
    handleRegelpositionUpdated,
    checkKeimgruppeUsedInSelected,
    checkKeimUsedInSelected,
    checkAnalyseUsage
  }
})
